/** Product types the whole-sofa size rules apply to. */
const SEATING_TYPE_PATTERN = /sofa|armchair|chaise|corner|love\s?seat|chair bed/;

/** Seating-family types that are a part rather than a seat, e.g. "Sofa legs". */
const SEATING_TYPE_EXCLUSION_PATTERN = /legs/;

/** Whole-sofa types that carry a seater count worth preferring. */
const WHOLE_SOFA_TYPES = ['sofa', 'sofa bed'];

/** Preferred seater counts, most preferred first. */
const PREFERRED_SIZES = ['3 seater', '2 seater'];

/**
 * Combined-listing option that names a piece better than its size does.
 * Mattress listings split by firmness, with bed size as a separate listing.
 */
const DESCRIBING_OPTION = 'firmness';

/**
 * Custom element representing a store display drawer.
 * Handles fetching store and on-display product data for one or more stores,
 * rendering it, and controlling drawer interactions.
 */
class StoreDisplayDrawer extends HTMLElement {
  /** Initialise the drawer and bind event handlers. */
  constructor() {
    super();
    this.handleKeyup = this.handleKeyup.bind(this);
    this.handleTriggerClick = this.handleTriggerClick.bind(this);
    this.handleToggleClick = this.handleToggleClick.bind(this);
  }

  /**
   * Fetch store data, initialise drawer elements, and set up event listeners.
   */
  async connectedCallback() {
    this.storeHandles = this.getAttribute('data-store-handles')
        ? JSON.parse(this.getAttribute('data-store-handles'))
        : [];
    this.currentModel = this.getAttribute('data-current-model');
    this.productScope = this.getAttribute('data-product-scope') === 'true';
    this.currentType = this.getAttribute('data-current-type') || '';
    this.currentSize = this.getAttribute('data-current-size') || '';
    this.modularListings = this.getAttribute('data-modular-listings')
        ? JSON.parse(this.getAttribute('data-modular-listings'))
        : {};
    this.labelSingle = this.getAttribute('data-label-single') || '';
    this.labelMultiple = this.getAttribute('data-label-multiple') || '';
    this.labelCard = this.getAttribute('data-label-card') || '';
    this.labelViewProduct = this.getAttribute('data-label-view-product') || '';
    this.labelGetDirections = this.getAttribute('data-label-get-directions') || '';
    this.showStoreAddress = this.getAttribute('data-show-store-address');
    this.targetZips = this.getAttribute('data-target-zips')
        ? JSON.parse(this.getAttribute('data-target-zips'))
        : [];

    if (!this.fetchPromise) {
      this.fetchPromise = this.fetchStoresData();
    }
    const fetchedStores = await this.fetchPromise;
    this.storesData = this.filterQualifyingStores(fetchedStores);
    if (!this.storesData || this.storesData.length === 0) {
      return;
    }

    this.sectionId = this.getAttribute('data-section-id');
    if (!this.dataset.reparented && this.parentElement && this.parentElement !== document.body) {
      this.dataset.reparented = 'true';
      document.body.appendChild(this);
      return;
    }

    this.drawerId = this.id || this.getAttribute('data-drawer-id');
    if (!this.drawerId) {
      this.drawerId = `store-display-drawer-${Math.random().toString(36).slice(2)}`;
      this.id = this.drawerId;
    }

    this.overlay = this.querySelector('[data-store-display-overlay]');
    this.closeButton = this.querySelector('[data-store-display-close]');
    this.drawerInner = this.querySelector('.drawer__inner');
    this.variantList = this.querySelector('[data-store-display-list]');
    this.headerSlot = this.querySelector('[data-store-display-header]');
    this.toggleButton = this.querySelector('[data-store-display-toggle]');
    this.toggleLabel = this.querySelector('[data-store-display-toggle-label]');

    this.viewAllExpandTemplate = this.toggleButton
        ? (this.toggleButton.getAttribute('data-label-expand') || 'View all')
        : 'View all';
    this.viewAllCollapseTemplate = this.toggleButton
        ? (this.toggleButton.getAttribute('data-label-collapse') || 'View less')
        : 'View less';

    if (this.toggleButton && this.variantList) {
      this.toggleButton.removeEventListener('click', this.handleToggleClick);
      this.toggleButton.addEventListener('click', this.handleToggleClick);
    }

    if (this.drawerInner && !this.drawerInner.hasAttribute('tabindex')) {
      this.drawerInner.setAttribute('tabindex', '-1');
    }

    if (!this.hasAttribute('aria-hidden')) {
      this.setAttribute('aria-hidden', 'true');
    }

    this.handleOverlayClick = this.handleOverlayClick || (() => this.close());
    this.handleCloseClick = this.handleCloseClick || (() => this.close());

    if (this.overlay) {
      this.overlay.removeEventListener('click', this.handleOverlayClick);
      this.overlay.addEventListener('click', this.handleOverlayClick);
    }

    if (this.closeButton) {
      this.closeButton.removeEventListener('click', this.handleCloseClick);
      this.closeButton.addEventListener('click', this.handleCloseClick);
    }

    this.registerTriggers();
    this.updateTriggerLabel();
    this.addEventListener('keyup', this.handleKeyup);

    if (!window.__storeDisplayGeoInitialized) {
      window.__storeDisplayGeoInitialized = true;
      this.pollGeo();
      document.addEventListener('shopify:section:load', (event) => {
        this.updateGeoVisibility(event.target);
        this.pollGeo();
      });
    } else {
      this.pollGeo();
    }
  }

  /**
   * Clean up event listeners
   */
  disconnectedCallback() {
    this.removeEventListener('keyup', this.handleKeyup);
    if (this.overlay) {
      this.overlay.removeEventListener('click', this.handleOverlayClick);
    }
    if (this.closeButton) {
      this.closeButton.removeEventListener('click', this.handleCloseClick);
    }
    if (this.triggers) {
      this.triggers.forEach((trigger) => trigger.removeEventListener('click', this.handleTriggerClick));
    }
    if (this.toggleButton) {
      this.toggleButton.removeEventListener('click', this.handleToggleClick);
    }
  }

  /**
   * Register triggers that open this drawer.
   */
  registerTriggers() {
    if (!this.drawerId) {
      return;
    }

    const selector = `[data-store-display-trigger="${this.drawerId}"]`;
    this.triggers = Array.from(document.querySelectorAll(selector));

    this.triggers.forEach((trigger) => {
      trigger.removeEventListener('click', this.handleTriggerClick);
      trigger.addEventListener('click', this.handleTriggerClick);
    });
  }

  /**
   * Handle click on a trigger to open the drawer.
   */
  handleTriggerClick(event) {
    event.preventDefault();
    const trigger = event.currentTarget;

    if (trigger.getAttribute('data-store-display-trigger') !== this.drawerId) {
      return;
    }

    this.open(trigger);
  }

  /**
   * Toggle expanded state of the single-store supplemental list.
   */
  handleToggleClick(event) {
    event.preventDefault();
    if (!this.variantList || !this.toggleButton || !this.toggleLabel) {
      return;
    }

    const isExpanded = this.toggleButton.getAttribute('aria-expanded') === 'true';
    const newExpanded = !isExpanded;
    this.toggleButton.setAttribute('aria-expanded', String(newExpanded));
    this.variantList.classList.toggle('is-expanded', newExpanded);

    const expandLabel = this.toggleButton.getAttribute('data-label-expand') || 'View all';
    const collapseLabel = this.toggleButton.getAttribute('data-label-collapse') || 'View less';
    this.toggleLabel.textContent = newExpanded ? collapseLabel : expandLabel;
  }

  /**
   * Handle keyup Escape events.
   */
  handleKeyup(event) {
    if (event.key === 'Escape' || event.code === 'Escape') {
      this.close();
    }
  }

  /**
   * Open drawer and trap focus.
   */
  open(trigger) {
    this.activeTrigger = trigger || this.activeTrigger;
    this.renderForTrigger(this.activeTrigger);

    document.body.classList.add('overflow-hidden');
    this.classList.add('active');
    this.setAttribute('aria-hidden', 'false');

    requestAnimationFrame(() => {
      const focusTarget = this.closeButton || this.drawerInner;

      if (focusTarget && typeof focusTarget.focus === 'function') {
        focusTarget.focus({ preventScroll: true });
      }

      if (typeof trapFocus === 'function' && this.drawerInner && focusTarget) {
        trapFocus(this.drawerInner, focusTarget);
      }
    });
  }

  /**
   * Close drawer and restore focus.
   */
  close() {
    this.classList.remove('active');
    this.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('overflow-hidden');

    if (typeof removeTrapFocus === 'function') {
      removeTrapFocus(this.activeTrigger);
    }

    if (this.activeTrigger && typeof this.activeTrigger.focus === 'function') {
      this.activeTrigger.focus({ preventScroll: true });
    }
  }

  /**
   * Fetch every qualifying store's metaobject + on-display products.
   * Returns an array of store-data objects (stores resolving to zero
   * products are dropped).
   */
  async fetchStoresData() {
    if (!this.storeHandles || this.storeHandles.length === 0) {
      return [];
    }

    const results = await Promise.all(
      this.storeHandles.map((handle) => this.fetchStore(handle))
    );

    return results.filter((store) => store && store.products && store.products.length > 0);
  }

  /**
   * Fetch a single store metaobject and its on-display products from the
   * Storefront API. Returns null on error so the remaining stores still render.
   */
  async fetchStore(handle) {
    const STOREFRONT_WORKER_ENDPOINT = 'https://storefront-proxy.swyft.workers.dev';

    const storeQuery = `
      query GetStoreMetaobject($handle: String!) {
        metaobject(handle: { handle: $handle, type: "store" }) {
          id
          handle
          fields {
            key
            value
          }
        }
      }
    `;

    try {
      const storeResponse = await fetch(STOREFRONT_WORKER_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ query: storeQuery, variables: { handle } }),
      });

      const storeJson = await storeResponse.json();

      if (storeJson.errors) {
        console.error(storeJson.errors);
        return null;
      }

      const metaobject = storeJson?.data?.metaobject;
      if (!metaobject) {
        return null;
      }

      const onDisplayField = metaobject.fields.find((field) => field.key === 'on_display_products');
      const productIds = onDisplayField ? JSON.parse(onDisplayField.value) : [];

      const productsQuery = `
        query GetOnDisplayProducts($ids: [ID!]!) {
          nodes(ids: $ids) {
            ... on Product {
              id
              title
              handle
              productType
              featuredImage {
                url(transform: {maxWidth: 300, maxHeight: 300}),
                altText
              }
              model: metafield(namespace: "swyft", key: "product_model") { value }
              seatingCapacity: metafield(namespace: "swyft", key: "product_seating_capacity") { value }
              variants(first: 1) {
                nodes {
                  selectedOptions { name value }
                }
              }
            }
          }
        }
      `;

      const productsResponse = await fetch(STOREFRONT_WORKER_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ query: productsQuery, variables: { ids: productIds } }),
      });

      const productsJson = await productsResponse.json();

      if (productsJson.errors) {
        console.error(productsJson.errors);
        return null;
      }

      const products = (productsJson?.data?.nodes || []).filter((node) => node && node.id);

      const fields = metaobject?.fields?.reduce((fieldObject, field) => {
        try {
          fieldObject[field.key] = JSON.parse(field.value);
        } catch {
          fieldObject[field.key] = field.value;
        }
        return fieldObject;
      }, {});

      return { ...metaobject, fields, products };
    } catch (error) {
      console.error(error);
      return null;
    }
  }

  /**
   * Model of an on-display product, as written. Falls back to the combined
   * listing's parent for products that carry it only at listing level.
   */
  productModelLabel(product) {
    return (product?.model?.value || '').trim();
  }

  /** Model of an on-display product, normalised for comparison. */
  productModelKey(product) {
    return this.productModelLabel(product).toLowerCase();
  }

  /** Product type of an on-display product, normalised. */
  productType(product) {
    return this.productTypeLabel(product).toLowerCase();
  }

  /** Product type of an on-display product, as written — e.g. "Corner sofa". */
  productTypeLabel(product) {
    return (product?.productType || '').trim();
  }

  /** Seater count of an on-display product, normalised — e.g. "3 seater". */
  productSize(product) {
    return this.productSizeLabel(product).toLowerCase();
  }

  /** Seater count of an on-display product, as written — e.g. "Kingsize". */
  productSizeLabel(product) {
    return (product?.seatingCapacity?.value || '').trim();
  }

  /**
   * One of the child's own combined-listing option values, as written — the
   * single variant carries the child's position in the listing's option grid.
   */
  productOptionValue(product, optionName) {
    const options = product?.variants?.nodes?.[0]?.selectedOptions || [];
    const match = options.find(
      (option) => (option?.name || '').toLowerCase().trim() === optionName
    );
    return (match?.value || '').trim();
  }

  /**
   * What sets an on-display product apart from the same model's other pieces:
   * the seater count for a whole sofa, otherwise the type. Keeps two cards of
   * one model from both reading "Model 06" — they read "3 Seater" and
   * "Armchair". Anything the model name already says is skipped, so "Bed 02"
   * reads "Kingsize" rather than "Bed". Title cased, since the underlying
   * metafield and taxonomy values are entered inconsistently.
   *
   * A listing that splits by firmness is named by it in preference to its size,
   * so a mattress reads "Mattress 03 Medium Soft" rather than "Mattress 03
   * Kingsize" — firmness is what a shopper is choosing between in a showroom.
   */
  productDescriptor(product) {
    const size = this.productSizeLabel(product);
    const type = this.productTypeLabel(product);
    const model = this.productModelLabel(product).toLowerCase();
    const candidates = WHOLE_SOFA_TYPES.includes(this.productType(product))
        ? [size, type]
        : [type, size];

    const firmness = this.productOptionValue(product, DESCRIBING_OPTION);
    if (firmness) {
      candidates.unshift(firmness);
    }

    const descriptor = candidates.find(
      (candidate) => candidate && !model.includes(candidate.toLowerCase())
    );

    return descriptor ? descriptor.replace(/\b\p{Ll}/gu, (letter) => letter.toUpperCase()) : '';
  }

  /**
   * The card heading for an on-display product — its model run through the
   * localised template, so a specific colourway on the showroom floor reads as
   * its generic model. Products carrying no model fall back to their title.
   */
  cardLabel(product, descriptor = '') {
    const model = [this.productModelLabel(product), descriptor].filter(Boolean).join(' ');

    if (!model || !this.labelCard) {
      return product.title || '';
    }

    return this.labelCard.replace('%MODEL%', model);
  }

  /**
   * Resolve one heading per product for a store surface, keyed by id. These
   * lists carry several pieces of one model, so each heading names the piece
   * too. Pieces that share a model, type and seater count — a model's arm and
   * corner modules, say — would still collide, so those fall back to titles.
   */
  genericCardLabels(products) {
    const labels = products.map((product) =>
      // A modular model's card stands for the whole model, so it is named by
      // the model alone rather than by the piece that happens to be on display.
      this.isModularModel(this.productModelKey(product))
          ? this.cardLabel(product)
          : this.cardLabel(product, this.productDescriptor(product))
    );
    const counts = labels.reduce(
      (tally, label) => tally.set(label, (tally.get(label) || 0) + 1),
      new Map()
    );

    return new Map(
      products.map((product, index) => [
        product.id,
        counts.get(labels[index]) > 1 ? product.title || labels[index] : labels[index],
      ])
    );
  }

  /**
   * Whether an on-display product belongs to the viewed listing — the same
   * model in the same type and seater count, in any colour. `parent_product`
   * would say so directly but is not exposed to the Storefront API, and ids
   * differ between the proxy's store and the one rendering the page.
   */
  isViewedListing(product) {
    return this.productModelKey(product) === this.currentModel
        && this.productType(product) === this.currentType
        && this.productSize(product) === this.currentSize;
  }

  /** Whether the whole-sofa size rules apply to this product. */
  isSeatingProduct(product) {
    const type = this.productType(product);
    if (!type || SEATING_TYPE_EXCLUSION_PATTERN.test(type)) return false;
    return SEATING_TYPE_PATTERN.test(type);
  }

  /**
   * Whether a model rolls its on-display pieces up into a single card. The
   * snippet decides, since it also resolves the listing such a card links to;
   * see `data-modular-listings` in snippets/store-display-drawer.liquid.
   */
  isModularModel(modelKey) {
    return Object.hasOwn(this.modularListings, modelKey);
  }

  /** Whether the viewed model matches on-display stock by model, not sibling. */
  usesModularRollup() {
    return this.isModularModel(this.currentModel);
  }

  /**
   * Collapse each modular model's on-display pieces down to the first the store
   * lists. Their listings are split across corners, chaises, modules and arms,
   * and one generic card stands for all of them.
   */
  bundleModular(products) {
    const seen = new Set();

    return products.filter((product) => {
      const modelKey = this.productModelKey(product);
      if (!this.isModularModel(modelKey)) return true;
      if (seen.has(modelKey)) return false;
      seen.add(modelKey);
      return true;
    });
  }

  /**
   * The on-display products a PDP highlights for one store.
   * A modular model matches any piece of itself and rolls up to one card.
   * Every other model highlights only siblings of the viewed listing — the
   * same model in the same size, in any colour.
   */
  pdpMatches(products) {
    if (this.usesModularRollup()) {
      return this.bundleModular(
        products.filter((product) => this.productModelKey(product) === this.currentModel)
      );
    }

    if (!this.currentModel) {
      return [];
    }

    return products.filter((product) => this.isViewedListing(product));
  }

  /**
   * The on-display products a store surface shows. Within each model, a whole
   * 3-seater, 2-seater or armchair stands in for the model's other seating
   * pieces; a model on display in none of those keeps the actual pieces.
   * Anything outside the seating family is left untouched.
   */
  genericSelection(products) {
    const groups = new Map();

    products.forEach((product) => {
      if (!this.isSeatingProduct(product)) return;
      const key = this.productModelKey(product) || product.id;
      if (!groups.has(key)) {
        groups.set(key, []);
      }
      groups.get(key).push(product);
    });

    const superseded = new Set();

    groups.forEach((group) => {
      const preferred = group.filter((product) => {
        const type = this.productType(product);
        if (type === 'armchair') return true;
        return WHOLE_SOFA_TYPES.includes(type) && PREFERRED_SIZES.includes(this.productSize(product));
      });

      if (preferred.length === 0) return;

      group.forEach((product) => {
        if (!preferred.includes(product)) {
          superseded.add(product.id);
        }
      });
    });

    // A listing named by its option value needs only one card per value: two
    // mattresses of one firmness are the same choice to a shopper, whatever bed
    // sizes they are, so the first the store lists stands for them.
    const seenOptionValues = new Set();

    products.forEach((product) => {
      const value = this.productOptionValue(product, DESCRIBING_OPTION);
      if (!value) return;

      const key = `${this.productModelKey(product)}|${value.toLowerCase()}`;
      if (seenOptionValues.has(key)) {
        superseded.add(product.id);
        return;
      }
      seenOptionValues.add(key);
    });

    return this.bundleModular(products.filter((product) => !superseded.has(product.id)));
  }

  /**
   * Keep only stores whose on-display products include a PDP match. Outside a
   * product context (e.g. the find-us surface), every fetched store qualifies.
   */
  filterQualifyingStores(stores) {
    if (!stores) return [];
    if (!this.productScope) return stores;
    return stores.filter((store) => this.pdpMatches(store.products || []).length > 0);
  }

  /**
   * Set the PDP trigger button label from the number of qualifying stores.
   * Skipped outside a product context so the find-us triggers (which have their
   * own labels) are left untouched.
   */
  updateTriggerLabel() {
    if (!this.productScope || !this.triggers || !this.storesData.length) {
      return;
    }

    let label;
    if (this.storesData.length === 1) {
      const storeName = this.storesData[0].fields.global_name || '';
      label = this.labelSingle.replace('%STORE%', storeName);
    } else {
      label = this.labelMultiple.replace('%COUNT%', String(this.storesData.length));
    }

    this.triggers.forEach((trigger) => {
      const target = trigger.querySelector('span') || trigger;
      target.textContent = label;
    });
  }

  /**
   * Render the drawer for the trigger that opened it. A trigger carrying a
   * `data-store-handle` (the find-us store cards) scopes the drawer to that one
   * store; otherwise all qualifying stores are shown (the PDP button).
   */
  renderForTrigger(trigger) {
    const handle = trigger && trigger.getAttribute
        ? trigger.getAttribute('data-store-handle')
        : null;

    let storesToRender = this.storesData;
    if (handle) {
      const matched = this.storesData.filter((store) => store.handle === handle);
      if (matched.length) {
        storesToRender = matched;
      }
    }

    this.renderStores(storesToRender);
  }

  /**
   * Render the given stores, choosing the single-store or multi-store layout.
   * Clears any previous render so the drawer can be reused across triggers.
   */
  renderStores(stores) {
    if (!this.variantList) {
      return;
    }

    this.variantList.innerHTML = '';
    if (this.headerSlot) {
      this.headerSlot.innerHTML = '';
    }
    this.variantList.classList.remove('store-display__store-list--collapsible', 'is-expanded');
    this.variantList.removeAttribute('data-store-display-collapsible');
    if (this.toggleButton) {
      this.toggleButton.setAttribute('aria-expanded', 'false');
      this.toggleButton.parentElement.style.display = 'none';
    }
    if (this.toggleLabel && this.toggleButton) {
      this.toggleLabel.textContent = this.toggleButton.getAttribute('data-label-expand') || 'View all';
    }

    if (stores.length === 1) {
      this.renderSingleStore(stores[0]);
    } else {
      this.renderMultiStore(stores);
    }
  }

  /**
   * Split a store's products into highlighted and supplemental markup.
   * On a PDP the highlighted set is the match for the viewed listing and the
   * rest of the store's stock sits behind the "View more" toggle. On a store
   * surface only the generic selection renders, with nothing supplemental.
   */
  splitProducts(store) {
    const products = store.products || [];

    if (!this.productScope) {
      const selection = this.genericSelection(products);
      const labels = this.genericCardLabels(selection);
      return {
        matched: selection.map((product) =>
          this.generateProductMarkup(product, true, labels.get(product.id))
        ),
        supplemental: [],
      };
    }

    const highlighted = this.pdpMatches(products);
    const highlightedIds = new Set(highlighted.map((product) => product.id));

    // A highlighted modular model is already represented by its one card, so
    // its remaining pieces drop out rather than reappearing below it.
    const highlightedModular = new Set(
      highlighted
        .map((product) => this.productModelKey(product))
        .filter((modelKey) => this.isModularModel(modelKey))
    );

    return {
      matched: highlighted.map((product) => this.generateProductMarkup(product, true)),
      supplemental: this.bundleModular(
        products.filter(
          (product) => !highlightedIds.has(product.id)
              && !highlightedModular.has(this.productModelKey(product))
        )
      ).map((product) => this.generateProductMarkup(product, false)),
    };
  }

  /**
   * Build the store header (name, subtitle, address, directions, phone).
   * Pass includeNameAddress: false to omit the name/address (used when those
   * already appear in a multi-store section's summary button).
   */
  buildStoreHeader(store, includeNameAddress = true) {
    const storeHeader = document.createElement('div');
    storeHeader.className = 'store-display__store-header';

    if (includeNameAddress && store.fields.global_name) {
      const storeName = document.createElement('h3');
      storeName.className = 'store-display__store-name h4';
      storeName.textContent = store.fields.global_name;
      storeHeader.appendChild(storeName);
    }

    if (store.fields.name_subtitle) {
      const subtitle = document.createElement('p');
      subtitle.className = 'store-display__store-subtitle';
      subtitle.textContent = store.fields.name_subtitle;
      storeHeader.appendChild(subtitle);
    }

    if (includeNameAddress && this.showStoreAddress && store.fields.global_address) {
      const address = document.createElement('p');
      address.className = 'store-display__store-address';
      address.textContent = store.fields.global_address;
      storeHeader.appendChild(address);
    }

    if (store.fields.global_latitude && store.fields.global_longitude) {
      const directionsAnchor = document.createElement('a');
      directionsAnchor.className = 'store-display__store-directions';
      directionsAnchor.target = '_blank';
      directionsAnchor.href = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(
          store.fields.global_latitude
      )},${encodeURIComponent(store.fields.global_longitude)}`;
      directionsAnchor.textContent = this.labelGetDirections;
      storeHeader.appendChild(directionsAnchor);
    }

    if (store.fields.global_phone_number) {
      const phoneAnchor = document.createElement('a');
      phoneAnchor.className = 'store-display__store-phone';
      phoneAnchor.href = `tel:${store.fields.global_phone_number}`;
      phoneAnchor.textContent = store.fields.global_phone_number;
      storeHeader.appendChild(phoneAnchor);
    }

    return storeHeader;
  }

  /**
   * Render the single-store layout into the existing store list, using the
   * shared "View all" supplemental toggle. Matches the pre-existing design.
   */
  renderSingleStore(store) {
    if (this.headerSlot) {
      this.headerSlot.appendChild(this.buildStoreHeader(store, true));
    }

    const storeLi = document.createElement('li');
    storeLi.className = 'store-display__store';
    storeLi.dataset.storeId = store.handle;

    const { matched, supplemental } = this.splitProducts(store);
    matched.forEach((product) => storeLi.appendChild(product));
    supplemental.forEach((product) => storeLi.appendChild(product));

    this.variantList.appendChild(storeLi);

    if (supplemental.length > 0 && this.toggleButton && this.toggleLabel) {
      const expandLabel = this.resolveViewLabel(this.viewAllExpandTemplate, store);
      const collapseLabel = this.resolveViewLabel(this.viewAllCollapseTemplate, store);
      this.toggleButton.setAttribute('data-label-expand', expandLabel);
      this.toggleButton.setAttribute('data-label-collapse', collapseLabel);
      this.toggleButton.setAttribute('aria-expanded', 'false');
      this.toggleLabel.textContent = expandLabel;
      this.toggleButton.parentElement.style.display = '';
      this.variantList.classList.add('store-display__store-list--collapsible');
      this.variantList.setAttribute('data-store-display-collapsible', '');
    } else {
      this.variantList.classList.remove('store-display__store-list--collapsible');
      this.variantList.removeAttribute('data-store-display-collapsible');
      if (this.toggleButton) {
        this.toggleButton.parentElement.style.display = 'none';
      }
    }
  }

  /**
   * Render the multi-store layout: one independently-collapsible section per
   * store (title + address in the summary, products hidden until expanded).
   */
  renderMultiStore(stores) {
    if (this.toggleButton) {
      this.toggleButton.parentElement.style.display = 'none';
    }

    const sorted = [...stores].sort((a, b) => {
      const orderA = parseInt(a.fields.sort_order, 10);
      const orderB = parseInt(b.fields.sort_order, 10);
      const hasA = !Number.isNaN(orderA);
      const hasB = !Number.isNaN(orderB);
      if (hasA && hasB && orderA !== orderB) return orderA - orderB;
      if (hasA && !hasB) return -1;
      if (!hasA && hasB) return 1;
      return (a.fields.global_name || '').localeCompare(b.fields.global_name || '');
    });

    sorted.forEach((store, index) => {
      this.variantList.appendChild(this.buildStoreSection(store, index));
    });
  }

  /**
   * Build one collapsible store section for the multi-store layout.
   */
  buildStoreSection(store, index) {
    const sectionLi = document.createElement('li');
    sectionLi.className = 'store-display__store store-display__store--accordion';
    sectionLi.dataset.storeId = store.handle;

    const regionId = `${this.drawerId}-store-${index}`;

    const toggle = document.createElement('button');
    toggle.type = 'button';
    toggle.className = 'store-display__store-toggle';
    toggle.setAttribute('aria-expanded', 'false');
    toggle.setAttribute('aria-controls', regionId);

    if (store.fields.global_name) {
      const name = document.createElement('span');
      name.className = 'store-display__store-name h4';
      name.textContent = store.fields.global_name;
      toggle.appendChild(name);
    }

    if (this.showStoreAddress && store.fields.global_address) {
      const address = document.createElement('span');
      address.className = 'store-display__store-address';
      address.textContent = store.fields.global_address;
      toggle.appendChild(address);
    }

    const region = document.createElement('div');
    region.id = regionId;
    region.className = 'store-display__store-region';
    region.hidden = true;

    region.appendChild(this.buildStoreHeader(store, false));

    const variantList = document.createElement('ul');
    variantList.className = 'store-display__variant-list';

    const { matched, supplemental } = this.splitProducts(store);
    matched.forEach((product) => variantList.appendChild(product));
    supplemental.forEach((product) => variantList.appendChild(product));

    region.appendChild(variantList);
    if (supplemental.length > 0) {
      variantList.classList.add('store-display__store-list--collapsible');
      region.appendChild(this.buildSectionViewAll(variantList, store));
    }

    toggle.addEventListener('click', () => {
      const isExpanded = toggle.getAttribute('aria-expanded') === 'true';
      toggle.setAttribute('aria-expanded', String(!isExpanded));
      region.hidden = isExpanded;
    });

    sectionLi.appendChild(toggle);
    sectionLi.appendChild(region);

    return sectionLi;
  }

  /**
   * Build a per-section "View all" toggle controlling that section's
   * supplemental (off-model) products.
   */
  /**
   * Resolve a label template, substituting %STORE% with the store's name.
   */
  resolveViewLabel(template, store) {
    const storeName = (store && store.fields && store.fields.global_name) || '';
    return (template || '').replace('%STORE%', storeName);
  }

  buildSectionViewAll(variantList, store) {
    const expandLabel = this.resolveViewLabel(this.viewAllExpandTemplate, store);
    const collapseLabel = this.resolveViewLabel(this.viewAllCollapseTemplate, store);

    const wrapper = document.createElement('div');
    wrapper.className = 'store-display__view-all';

    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'link';
    button.setAttribute('aria-expanded', 'false');

    const label = document.createElement('span');
    label.textContent = expandLabel;
    button.appendChild(label);

    button.addEventListener('click', (event) => {
      event.preventDefault();
      const isExpanded = button.getAttribute('aria-expanded') === 'true';
      const newExpanded = !isExpanded;
      button.setAttribute('aria-expanded', String(newExpanded));
      variantList.classList.toggle('is-expanded', newExpanded);
      label.textContent = newExpanded ? collapseLabel : expandLabel;
    });

    wrapper.appendChild(button);
    return wrapper;
  }

  /**
   * Where a card sends shoppers and which image it carries. A modular model's
   * card stands for the whole model whichever piece is on the showroom floor,
   * so it takes the snippet's resolved chaise child rather than the piece's own
   * url and photo.
   */
  cardTarget(product) {
    const modular = this.modularListings[this.productModelKey(product)];
    if (modular && modular.url) {
      return { url: modular.url, imageUrl: modular.image || '' };
    }

    return {
      url: `/products/${product.handle || ''}`,
      imageUrl: product.featuredImage?.url || '',
    };
  }

  /**
   * Generate HTML for an on-display product (a combined-listing child).
   * The card is labelled by model rather than product title, so a specific
   * colourway on the showroom floor reads as its generic model. Store surfaces
   * pass a label naming the piece as well, since they list several per model.
   */
  generateProductMarkup(product, isMatch = true, label = null) {
    const heading = label || this.cardLabel(product);
    const target = this.cardTarget(product);

    const productLi = document.createElement('li');
    productLi.className = isMatch
        ? 'store-display__variant'
        : 'store-display__variant store-display__variant--supplemental';

    const productMedia = document.createElement('div');
    productMedia.className = 'store-display__variant-media';
    if (target.imageUrl) {
      const img = document.createElement('img');
      img.src = target.imageUrl;
      img.alt = target.imageUrl === product.featuredImage?.url
          ? product.featuredImage.altText || product.title || ''
          : heading;
      img.loading = 'lazy';
      img.width = 160;
      img.height = 160;
      productMedia.appendChild(img);
    }

    const productDetails = document.createElement('div');
    productDetails.className = 'store-display__variant-details';

    const productTitle = document.createElement('p');
    productTitle.className = 'store-display__variant-product h5';
    productTitle.textContent = heading;
    productDetails.appendChild(productTitle);

    const productAnchor = document.createElement('a');
    productAnchor.className = 'store-display__variant-link link';
    productAnchor.href = target.url;
    productAnchor.textContent = this.labelViewProduct;
    productDetails.appendChild(productAnchor);

    productLi.appendChild(productMedia);
    productLi.appendChild(productDetails);

    return productLi;
  }

  /**
   * Update visibility of the 'View in store' button on the product page.
   */
  updateGeoVisibility(root = document) {
    const scope = root;
    let show = false;

    const hasQualifyingStores = (this.storesData || []).length > 0;

    if (hasQualifyingStores) {
      let areaList = Array.isArray(this.targetZips)
        ? this.targetZips
        : (this.targetZips || '').split(',');

      areaList = areaList
        .map((a) => a.toString().trim().toUpperCase())
        .filter((a) => a.length);

      if (areaList.length === 0) {
        // No postcode catchment configured: show wherever the product is on display.
        show = true;
      } else if (window.__GEO__ && typeof window.__GEO__.zip === 'string') {
        const geoPostcode = window.__GEO__.zip.trim().toUpperCase();
        show = areaList.some((area) => {
          const escapedArea = area.replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&');
          const pattern = new RegExp('^' + escapedArea + '\\d', 'i');
          return pattern.test(geoPostcode);
        });
      }
    }

    const nodes = scope.querySelectorAll('[data-store-display-geo]');
    nodes.forEach((node) => node.hidden = !show);
  }

  /**
   * Poll for geolocation data until available or maximum attempts reached.
   */
  pollGeo(attempt = 0) {
    this.updateGeoVisibility();

    if (window.__GEO__ && typeof window.__GEO__.city === 'string') return;
    if (attempt >= 40) return;

    setTimeout(() => this.pollGeo(attempt + 1), 250);
  }
}

if (!customElements.get('store-display-drawer')) {
  customElements.define('store-display-drawer', StoreDisplayDrawer);
}

document.addEventListener('shopify:section:load', (event) => {
  event.target.querySelectorAll('store-display-drawer').forEach((drawer) => {
    if (drawer && typeof drawer.registerTriggers === 'function') {
      drawer.registerTriggers();
    }
  });
});

document.addEventListener('shopify:section:unload', (event) => {
  const sectionId = event?.detail?.sectionId;
  if (!sectionId) {
    return;
  }

  document.querySelectorAll('store-display-drawer[data-section-id="' + sectionId + '"]').forEach((drawer) => {
    drawer.remove();
  });
});
